import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './AllCardsPopup.css';

const CLOSE_MS = 300; // синхронізовано з анімацією закриття в AllCardsPopup.css

// Розкладка сітки — синхронізовано з .acp-tile в AllCardsPopup.css
const TILE_H = 156;
const GAP = 16;
const ROW_STEP = TILE_H + GAP;

const LONG_PRESS_MS = 350; // скільки тримати, щоб «підняти» картку
const MOVE_SLOP = 8; // зсув пальця до спрацювання, після якого це вже скрол

function slotTransform(index) {
  const col = index % 2;
  const row = Math.floor(index / 2);
  return `translate(${col ? 'calc(100% + 16px)' : '0px'}, ${row * ROW_STEP}px)`;
}

// Попап «Усі картки» — сітка карток користувача і кнопка «Відкрити картку».
// Довге натискання на картку дозволяє перетягнути її на інше місце.
// cards: [{ id, name, last4, balance, icon }] — уже в збереженому порядку;
// icon — з public/card-icons, без нього — жовта заглушка.
// onReorder(ids) — картку відпустили на новому місці (зберігається в БД,
// за цим порядком ідуть і картки на головній).
// onSelectCard(id) — короткий тап по картці: попап закривається, головна гортає до неї.
export default function AllCardsPopup({ cards, onClose, onOpenNewCard, onReorder, onSelectCard }) {
  const [closing, setClosing] = useState(false);
  const [order, setOrder] = useState(() => cards.map((c) => c.id));
  const [drag, setDrag] = useState(null); // { id, x, y } — позиція піднятої картки в сітці
  const timerRef = useRef(null);
  const gridRef = useRef(null);
  const orderRef = useRef(order);
  const gestureRef = useRef(null);

  orderRef.current = order;

  const orderedCards = useMemo(() => {
    const byId = new Map(cards.map((c) => [c.id, c]));
    const known = order.filter((id) => byId.has(id));
    const missing = cards.filter((c) => !known.includes(c.id)).map((c) => c.id);
    return [...known, ...missing].map((id) => byId.get(id));
  }, [cards, order]);

  useEffect(() => () => {
    clearTimeout(timerRef.current);
    gestureRef.current?.cleanup();
  }, []);

  function close() {
    if (closing) return;
    setClosing(true);
    timerRef.current = setTimeout(onClose, CLOSE_MS);
  }

  function startGesture(e, id) {
    if (e.button !== 0 || gestureRef.current) return;
    const g = {
      id,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      active: false,
      startOrder: orderRef.current,
    };

    g.timer = setTimeout(() => {
      const gridRect = gridRef.current.getBoundingClientRect();
      const tileW = (gridRect.width - GAP) / 2;
      const index = orderRef.current.indexOf(id);
      const slotX = (index % 2) * (tileW + GAP);
      const slotY = Math.floor(index / 2) * ROW_STEP;
      Object.assign(g, {
        active: true,
        gridRect,
        tileW,
        offX: g.startX - gridRect.left - slotX,
        offY: g.startY - gridRect.top - slotY,
      });
      if (navigator.vibrate) navigator.vibrate(10);
      setDrag({ id, x: slotX, y: slotY });
    }, LONG_PRESS_MS);

    const onMove = (ev) => {
      if (ev.pointerId !== g.pointerId) return;
      if (!g.active) {
        if (Math.hypot(ev.clientX - g.startX, ev.clientY - g.startY) > MOVE_SLOP) end();
        return;
      }
      const x = ev.clientX - g.gridRect.left - g.offX;
      const y = ev.clientY - g.gridRect.top - g.offY;
      setDrag({ id, x, y });

      // слот, над яким зараз центр піднятої картки
      const n = orderRef.current.length;
      const rows = Math.ceil(n / 2);
      const col = x + g.tileW / 2 < g.gridRect.width / 2 ? 0 : 1;
      const row = Math.min(Math.max(Math.floor((y + TILE_H / 2) / ROW_STEP), 0), rows - 1);
      const target = Math.min(row * 2 + col, n - 1);
      const from = orderRef.current.indexOf(id);
      if (target !== from) {
        const next = orderRef.current.filter((cid) => cid !== id);
        next.splice(target, 0, id);
        orderRef.current = next;
        setOrder(next);
      }
    };

    // поки картка піднята — не даємо iOS скролити сторінку
    const onTouchMove = (ev) => {
      if (g.active) ev.preventDefault();
    };

    const end = () => {
      g.cleanup();
      if (g.active) {
        if (onReorder && orderRef.current.join() !== g.startOrder.join()) onReorder(orderRef.current);
        setDrag(null);
      }
    };

    const onUp = (ev) => {
      if (ev.pointerId !== g.pointerId) return;
      // Відпустили до довгого натискання і без зсуву — це тап, а не перетягування
      const isTap = !g.active && ev.type === 'pointerup';
      end();
      if (isTap && onSelectCard) {
        onSelectCard(id);
        close();
      }
    };

    g.cleanup = () => {
      clearTimeout(g.timer);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      gestureRef.current = null;
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    gestureRef.current = g;
  }

  const rows = Math.ceil(orderedCards.length / 2);

  return createPortal((
    <div className={`acp-root ${closing ? 'closing' : ''}`}>
      <div className="acp-bg" />
      <div className="acp-content">
        <button type="button" className="acp-close" onClick={() => close()} aria-label="Закрити">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 4l16 16M20 4L4 20" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <h1 className="acp-title">Усі картки</h1>
        <div
          className="acp-grid"
          ref={gridRef}
          style={{ height: rows ? rows * ROW_STEP - GAP : 0 }}
        >
          {orderedCards.map((card, index) => {
            const lifted = drag && drag.id === card.id;
            return (
              <div
                className={`acp-tile ${lifted ? 'lifted' : ''}`}
                key={card.id}
                style={{
                  transform: lifted
                    ? `translate(${drag.x}px, ${drag.y}px) scale(1.06) rotate(-2deg)`
                    : slotTransform(index),
                }}
                onPointerDown={(e) => startGesture(e, card.id)}
                onContextMenu={(e) => e.preventDefault()}
              >
                {card.icon
                  ? <img className="acp-mini-card" src={card.icon} alt="" draggable={false} />
                  : <div className="acp-mini-card acp-mini-card--placeholder" />}
                {card.last4 && <span className="acp-last4">** {card.last4}</span>}
                <p className="acp-name">{card.name}</p>
                <p className="acp-balance">{card.balance}</p>
              </div>
            );
          })}
        </div>
      </div>
      <button type="button" className="acp-open-btn" onClick={() => { if (onOpenNewCard() !== false) close(); }}>
        <span className="acp-open-plus" />
        Відкрити картку
      </button>
    </div>
  ), document.body);
}
